import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

/**
 * Certificate-of-Insurance header — the configurable issuer/header block printed on the COI.
 * Two kinds of rows:
 *   - Main / Partner  — clientId == null; the default for ALL partners (and Client-API fallback).
 *   - Client-specific — clientId == <a client>; overrides the default for that client's members.
 *
 * Resolution at print time: client-specific active row → Main/Partner active row (highest Id wins).
 * Any field left blank falls back to the member's dynamic value, so a blank header renders the
 * certificate exactly as before. Reads are normalized defensively (PascalCase → camelCase).
 */

// ===================== models =====================
export interface CoiHeader {
  id: number;
  clientId?: number | null;
  clientName?: string | null;
  masterPolicyId?: number | null;
  masterPolicyName?: string | null;
  masterPolicyNumber?: string | null;
  certificateNo?: string | null;
  masterPolicyNo?: string | null;
  insuranceCompany?: string | null;
  mainCompany?: string | null;
  policyServingOffice?: string | null;
  insuredName?: string | null;
  insuredAddress?: string | null;
  intermediaryName?: string | null;
  intermediaryCode?: string | null;
  periodofInsurance?: string | null;
  place?: string | null;
  remarks?: string | null;
  isActive: boolean;
  createdOn?: string | null;
}
export interface CoiHeaderDto {
  id: number; // 0 => insert, >0 => update
  clientId?: number | null; // null/0 => Main / Partner default (all partners)
  masterPolicyId?: number | null; // null/0 => client-wide; else that master policy (requires clientId)
  certificateNo?: string | null;
  masterPolicyNo?: string | null;
  insuranceCompany?: string | null;
  mainCompany?: string | null;
  policyServingOffice?: string | null;
  insuredName?: string | null;
  insuredAddress?: string | null;
  intermediaryName?: string | null;
  intermediaryCode?: string | null;
  periodofInsurance?: string | null;
  place?: string | null;
  remarks?: string | null;
  isActive: boolean;
}

// ===================== PascalCase → camelCase normalization =====================
function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function asNumOrNull(v: any): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asStr(v: any): string | null {
  return v == null ? null : String(v);
}

function mapRow(x: any): CoiHeader {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    clientId: asNumOrNull(pick(x, 'ClientId', 'clientId')),
    clientName: asStr(pick(x, 'ClientName', 'clientName')),
    masterPolicyId: asNumOrNull(pick(x, 'MasterPolicyId', 'masterPolicyId')),
    masterPolicyName: asStr(pick(x, 'MasterPolicyName', 'masterPolicyName')),
    masterPolicyNumber: asStr(pick(x, 'MasterPolicyNumber', 'masterPolicyNumber')),
    certificateNo: asStr(pick(x, 'CertificateNo', 'certificateNo')),
    masterPolicyNo: asStr(pick(x, 'MasterPolicyNo', 'masterPolicyNo')),
    insuranceCompany: asStr(pick(x, 'InsuranceCompany', 'insuranceCompany')),
    mainCompany: asStr(pick(x, 'MainCompany', 'mainCompany')),
    policyServingOffice: asStr(pick(x, 'PolicyServingOffice', 'policyServingOffice')),
    insuredName: asStr(pick(x, 'InsuredName', 'insuredName')),
    insuredAddress: asStr(pick(x, 'InsuredAddress', 'insuredAddress')),
    intermediaryName: asStr(pick(x, 'IntermediaryName', 'intermediaryName')),
    intermediaryCode: asStr(pick(x, 'IntermediaryCode', 'intermediaryCode')),
    periodofInsurance: asStr(pick(x, 'PeriodofInsurance', 'periodofInsurance')),
    place: asStr(pick(x, 'Place', 'place')),
    remarks: asStr(pick(x, 'Remarks', 'remarks')),
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    createdOn: asStr(pick(x, 'CreatedOn', 'createdOn')),
  };
}

function mapList(res: any): ApiEnvelope<CoiHeader[]> {
  return res && Array.isArray(res.data) ? { ...res, data: res.data.map(mapRow) } : { ...res, data: [] };
}
function mapObj(res: any): ApiEnvelope<CoiHeader> {
  return res?.data && typeof res.data === 'object' ? { ...res, data: mapRow(res.data) } : res;
}

@Injectable({ providedIn: 'root' })
export class CoiHeaderService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl; // ends in /api

  all() {
    return this.http.get<ApiEnvelope<any[]>>(`${this.api}/CoiHeader/GetAll`).pipe(map(mapList));
  }
  get(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/CoiHeader/GetById`, { params: { id } }).pipe(map(mapObj));
  }
  /** A client's own row(s) PLUS the Main / Partner default — "what applies to this client". */
  byClient(clientId: number) {
    return this.http.get<ApiEnvelope<any[]>>(`${this.api}/CoiHeader/GetByClient`, { params: { clientId } }).pipe(map(mapList));
  }
  insert(m: CoiHeaderDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/CoiHeader/Insert`, m);
  }
  update(m: CoiHeaderDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/CoiHeader/Update`, m);
  }
  enable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/CoiHeader/Enable`, { params: { id } });
  }
  disable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/CoiHeader/Disable`, { params: { id } });
  }
  delete(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/CoiHeader/Delete`, { params: { id } });
  }
}
